import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  MoreVertical,
  MessageSquare,
  RefreshCw,
  Loader2,
  AlertCircle,
  ChevronLeft,
} from "lucide-react";
// ...existing code...
import { getChannelIcon, getMediaIcon } from "../../data/mockChatData.jsx";
import { useConversations } from "../../hooks/useConversations";
import ConversationItem from "./ConversationItem";

const ChatSidebar = ({
  selectedChatId,
  onChatSelect,
  onToggleSidebar,
  className = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // 🎯 REAL BACKEND INTEGRATION
  const {
    conversations,
    stats,
    loading,
    error,
    isConnected,
    pagination,
    filters,
    updateFilters,
    selectConversation,
    loadMore,
    loadingMore,
    refresh,
    handleOptimisticMessage,
    handleMarkAsRead,
  } = useConversations();

  // Update search filter with debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      // No need to call updateFilters since we're doing client-side search
      // Just trigger local filtering which is already handled by filteredConversations
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Handle conversation selection
  const handleChatSelect = (conversationId) => {
    selectConversation(conversationId);
    onChatSelect(conversationId);
  };

  // Handle status filter change
  const handleStatusFilter = (status) => {
    updateFilters({ status });
  };

  // Filter conversations by search term AND status (client-side for immediate feedback)
  const filteredConversations = conversations.filter((conv) => {
    // 🔥 NEW: Status filtering based on active tab
    const matchesStatus =
      filters.status === "all" || conv.status === filters.status;

    // Search term filtering
    const matchesSearch =
      !searchTerm ||
      conv.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.last_message?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  // 🐛 DEBUG: Log filtering results
  React.useEffect(() => {
    console.log("🔍 [ChatSidebar] Filtering results:", {
      activeTab: filters.status,
      totalConversations: conversations.length,
      filteredCount: filteredConversations.length,
      hiddenCount: conversations.length - filteredConversations.length,
      searchTerm: searchTerm,
      hiddenConversations: conversations
        .filter((conv) => {
          const matchesStatus =
            filters.status === "all" || conv.status === filters.status;
          return !matchesStatus;
        })
        .map((conv) => ({
          id: conv.id,
          status: conv.status,
          customer_name: conv.customer_name,
        })),
    });
  }, [
    conversations.length,
    filteredConversations.length,
    filters.status,
    searchTerm,
  ]);

  // 🔍 DEBUG: Log tab switches
  React.useEffect(() => {
    console.log("🔄 [ChatSidebar] Tab switched to:", filters.status);
    if (filters.status === "escalated") {
      console.log(
        "⚡ [ChatSidebar] Escalated tab active - badge count:",
        stats.escalated
      );
    }
  }, [filters.status, stats.escalated]);

  // Conversations are already sorted in useConversations hook
  // Just use filtered conversations directly
  const sortedConversations = filteredConversations;

  // Memoized conversation item renderer for performance
  const renderConversationItem = React.useCallback(
    (conversation) => {
      const isSelected = selectedChatId === conversation.id;
      const mediaIcon = getMediaIcon(conversation.last_message_media_type);
      const channelIcon = getChannelIcon(
        conversation.channel_name || "unknown"
      );

      return (
        <ConversationItem
          key={conversation.id}
          conversation={conversation}
          isSelected={isSelected}
          mediaIcon={mediaIcon}
          channelIcon={channelIcon}
          onSelect={handleChatSelect}
          onMarkAsRead={handleMarkAsRead}
        />
      );
    },
    [selectedChatId, handleChatSelect, handleMarkAsRead]
  );

  return (
    <div
      className={`w-full sm:w-80 lg:w-80 xl:w-96 min-w-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col ${className}`}
    >
      {/* Header */}
      <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Sidebar toggle button - mobile only */}
            {onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                className="lg:hidden p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
                title="Open menu"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            )}
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
              Conversations
            </h2>
            {/* Connection status indicator */}
            <div
              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                isConnected ? "bg-green-500" : "bg-red-500"
              }`}
              title={isConnected ? "Connected" : "Disconnected"}
            />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={refresh}
              disabled={loading}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Refresh conversations"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 text-gray-500 dark:text-gray-400 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              )}
            </button>
            <MoreVertical className="w-5 h-5 text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300" />
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm sm:text-base bg-gray-100 dark:bg-gray-700 border-0 rounded-lg focus:ring-2 focus:ring-green-500 focus:bg-white dark:focus:bg-gray-600"
          />
        </div>

        {/* Status Filter */}
        <div
          className="overflow-x-auto overflow-y-hidden scrollbar-hide -mx-1 px-1 pb-1"
          style={{
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <div className="flex gap-1 w-max min-w-full">
            {[
              "escalated",
              "assigned",
              "active",
              "resolved",
              "disable",
              "all",
            ].map((status) => {
              let unreadBadge = null;
              if (status === "escalated") {
                const totalUnread = Number(stats.pending_escalations) || 0;
                if (totalUnread > 0) {
                  unreadBadge = (
                    <span className="bg-green-500 text-white text-xs rounded-full w-[14px] h-[14px] flex items-center justify-center flex-shrink-0">
                      {totalUnread}
                    </span>
                  );
                }
              } else if (status === "assigned") {
                const totalUnread = Number(stats.assigned_with_unread) || 0;
                if (totalUnread > 0) {
                  unreadBadge = (
                    <span className="bg-green-500 text-white text-xs rounded-full w-[14px] h-[14px] flex items-center justify-center flex-shrink-0">
                      {totalUnread}
                    </span>
                  );
                }
              }
              return (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-2.5 py-1.5 text-xs rounded-full transition-colors capitalize whitespace-nowrap flex-shrink-0 flex items-center gap-1 touch-manipulation ${
                    filters.status === status
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  {status}
                  {unreadBadge}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
          <button
            onClick={refresh}
            className="text-xs text-red-700 dark:text-red-300 hover:underline mt-1"
          >
            Try again
          </button>
        </div>
      )}

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {loading && conversations.length === 0 ? (
          /* Loading State */
          <div className="flex flex-col items-center justify-center h-full p-6">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading conversations...
            </p>
          </div>
        ) : sortedConversations.length > 0 ? (
          <>
            {sortedConversations.map((conversation) =>
              renderConversationItem(conversation)
            )}

            {/* Load More Button */}
            {pagination.hasMore && (
              <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
                >
                  {loadingMore ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading more...
                    </div>
                  ) : (
                    `Load more (${
                      pagination.total - sortedConversations.length
                    } remaining)`
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              No conversations found
            </h3>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {searchTerm
                ? "Try adjusting your search terms"
                : "New conversations will appear here"}
            </p>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Total: {pagination.total}</span>
          <span>Active: {stats.active}</span>
          <span>Escalated: {stats.escalated}</span>
        </div>
      </div>
    </div>
  );
};

export default ChatSidebar;
